import { useQuery } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { IAllLeadsResponse } from "../models/Lead";
import apiConfig from "../utils/apiConfig";

export interface ILeadExportFilters {
  // yyyy-MM-dd. The API compares on the date part only, so no timezone shift can
  // move a lead into or out of the range the user picked.
  startDate: string | null;
  endDate: string | null;
  includeExported?: boolean;
}

// Backs the CSV export. /lead/partialforexport returns only the leads inside the chosen
// window that are actually going into the file, unlike /lead/partial which returned every
// lead in the system for the browser to filter down.
export default function useLeadsForExport(
  filters: ILeadExportFilters,
  componentName = "useLeadsForExport"
) {
  const { get } = useApiHelper(apiConfig(componentName));

  const leadsForExport = useQuery({
    queryKey: ["leads", "export", filters.startDate, filters.endDate, filters.includeExported ?? false],
    queryFn: () =>
      get<IAllLeadsResponse>(
        `/lead/partialforexport?startDate=${filters.startDate}&endDate=${filters.endDate}&includeExported=${filters.includeExported ?? false}`
      ),
    enabled: !!filters.startDate && !!filters.endDate,
    // Overrides the app-wide 2 minute staleTime. Pressing Lookup on a range that was looked up
    // before re-enables a query whose key is already cached, and a non-zero staleTime would serve
    // that cached count without asking the API — wrong for a button whose job is to go and count.
    staleTime: 0,
    // ...but permanently-stale data plus the app-wide refetchOnWindowFocus would re-run the whole
    // range query every time the tab regains focus with the modal open. This query is expensive and
    // only ever runs on an explicit Lookup, so focus must not trigger it.
    refetchOnWindowFocus: false,
  });

  return {
    leadsForExport,
  };
}
